import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as ReactLink } from "react-router-dom";
import YooptaEditor, { createYooptaEditor, useYooptaEditor } from "@yoopta/editor";
import { html as yooptaHTML } from "@yoopta/exports";

import Toolbar, { DefaultToolbarRender } from "@yoopta/toolbar";
import ActionMenu, { DefaultActionMenuRender } from "@yoopta/action-menu-list";

import Paragraph from "@yoopta/paragraph";
import { HeadingOne, HeadingTwo, HeadingThree } from "@yoopta/headings";
import { BulletedList, NumberedList, TodoList } from "@yoopta/lists";
import Image from "@yoopta/image";
import Link from "@yoopta/link";
import Embed from "@yoopta/embed";
import Video from "@yoopta/video";
import { Bold, Italic, Underline, Strike, CodeMark, Highlight } from "@yoopta/marks";

import { useAuth } from "../../context/AuthContext";
import UploadImage from "../../utils/UploadImage";

/* Layout-CSS */
const NY_CSS = `
:root{--ny-bg:#f6f7f8;--ny-card:#fff;--ny-border:#e6e8eb;--ny-text:#0f172a;--ny-muted:#6b7280;--ny-primary:#0f172a}
.ny-editor{background:var(--ny-bg);min-height:100vh}
.ny-container{max-width:1120px;margin:0 auto;padding:16px}
.ny-card{background:var(--ny-card);border:1px solid var(--ny-border);border-radius:16px;overflow:visible;position:relative}
.ny-header{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:16px;border-bottom:1px solid var(--ny-border)}
.ny-title{font-size:20px;font-weight:700;color:var(--ny-text)}
.ny-sub{font-size:12px;color:var(--ny-muted)}
.ny-toolbar{display:flex;gap:8px;align-items:center}
.ny-grid{display:grid;grid-template-columns:1fr;gap:0}
.ny-section{padding:16px}
@media (min-width:1024px){.ny-grid{grid-template-columns:420px 1fr}.ny-section + .ny-section{border-left:1px solid var(--ny-border)}}
.ny-form{display:grid;grid-template-columns:1fr;gap:12px}
.ny-field{display:flex;flex-direction:column;gap:6px}
.ny-label{font-size:13px;color:var(--ny-muted)}
.ny-input,.ny-textarea{border:1px solid var(--ny-border);border-radius:12px;padding:10px 12px;font-size:14px;background:#fff}
.ny-input:focus,.ny-textarea:focus{outline:2px solid #dbeafe;border-color:#93c5fd}
.ny-row{display:flex;gap:12px;flex-wrap:wrap;align-items:center}
.ny-chip{display:inline-flex;align-items:center;border:1px solid var(--ny-border);border-radius:999px;padding:2px 8px;font-size:12px;color:var(--ny-muted)}
.ny-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;z-index:40}
.ny-modal{width:min(960px,92vw);max-height:90vh;overflow:auto;background:#fff;border-radius:16px;border:1px solid var(--ny-border)}
.ny-modal-header{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid var(--ny-border);position:sticky;top:0;background:#fff;z-index:1}
.ny-modal-body{padding:20px}
.ny-close{border:none;background:transparent;font-size:22px;cursor:pointer;line-height:1}
.ny-article{max-width:860px;margin:0 auto}
.ny-hero{width:100%;height:auto;border-radius:12px;display:block;margin:16px 0 24px}
.ny-h1{font-size:clamp(28px,4.8vw,40px);font-weight:800;margin:8px 0 10px;line-height:1.1}
.ny-authorrow{display:flex;align-items:center;gap:10px;color:#4b5563;font-size:14px;margin-bottom:12px}
.ny-authorrow img{width:40px;height:40px;border-radius:999px;border:1px solid var(--ny-border)}
.ny-subtitle{font-size:18px;color:#4b5563;margin:6px 0 12px}
.ny-meta{display:flex;gap:8px;flex-wrap:wrap;color:#6b7280;font-size:14px;margin-bottom:16px}

.ny-modal--wide{width:100vw !important;max-width:none !important;height:100vh !important;max-height:none !important;border-radius:0 !important;border:0 !important}
.ny-modal--wide .ny-modal-body{padding:0 !important}
.ny-modal--wide .ny-article{max-width:none !important;margin:0 !important}

/* Yoopta volle Breite */
.ny-yoopta-wide,
.ny-yoopta-wide .yoopta-editor,
.ny-yoopta-wide [data-yoopta-editor],
.ny-yoopta-wide .yoopta-content,
.ny-yoopta-wide [data-yoopta-content],
.ny-yoopta-wide .yoopta-block{
  width:100% !important;max-width:none !important;margin-left:0 !important;margin-right:0 !important
}
.ny-back {
  margin-bottom: 1rem;
}
`;

function useInjectStyles(css, id = "ny-protected-yoopta-styles") {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = css;
    document.head.appendChild(style);
  }, [css, id]);
}

const PLUGINS = [Paragraph, HeadingOne, HeadingTwo, HeadingThree, BulletedList, NumberedList, TodoList, Image, Link, Embed, Video];
const MARKS = [Bold, Italic, Underline, Strike, CodeMark, Highlight];

function EditorReady({ onReady }) {
  const ed = useYooptaEditor();
  useEffect(() => { requestAnimationFrame(() => onReady?.(ed)); }, []); // eslint-disable-line
  return null;
}

function stripAllWrappers(raw) {
  if (!raw) return "";
  let s = String(raw);
  s = s.replace(/<script[\s\S]*?<\/script>/gi, "").replace(/<style[\s\S]*?<\/style>/gi, "");
  ["html", "head", "body", "meta", "title"].forEach((t) => { s = s.replace(new RegExp(`<${t}[^>]*>`, "gi"), "").replace(new RegExp(`</${t}>`, "gi"), ""); });
  try { const d = new DOMParser().parseFromString(s, "text/html"); s = d?.body?.innerHTML ?? s; } catch { }
  ["html", "head", "body", "meta", "title"].forEach((t) => { s = s.replace(new RegExp(`<${t}[^>]*>`, "gi"), "").replace(new RegExp(`</${t}>`, "gi"), ""); });
  return s.trim();
}
function decodeIfEscaped(html) { if (!html) return ""; if (!/[&]lt;|[&]gt;/.test(html)) return html; const el = document.createElement("textarea"); el.innerHTML = html; return el.value; }
function reduceHtmlToBasicSubset(s) {
  if (!s) return "<p></p>"; let out = String(s);
  out = out.replace(/<(?!\/?(h1|h2|h3|p|br|ul|ol|li|b|strong|i|em|u|s|strike|a|img|iframe)(\s|>|\/))/gi, "&lt;")
    .replace(/<\/(?!h1|h2|h3|p|br|ul|ol|li|b|strong|i|em|u|s|strike|a|iframe)>/gi, "");
  out = out.replace(/<a\s+([^>]*href=['"][^'"]+['"][^>]*)>/gi, (m, attrs) => { if (!/target=/.test(attrs)) attrs += ' target="_blank"'; if (!/rel=/.test(attrs)) attrs += ' rel="noopener noreferrer"'; return `<a ${attrs}>`; });
  out = out.replace(/<iframe([^>]*)>([\s\S]*?)<\/iframe>/gi, (m, attrs) => { const src = (attrs.match(/src=["']([^"']+)["']/i) || [, ""])[1]; if (!src) return ""; return `<iframe src="${src}" loading="lazy" referrerpolicy="no-referrer-when-downgrade" allowfullscreen></iframe>`; });
  out = out.replace(/(<br\s*\/?>\s*){2,}/gi, "</p><p>");
  if (!out.trim()) out = "<p></p>"; if (!/^<\s*(h1|h2|h3|p|ul|ol)/i.test(out)) out = `<p>${out}</p>`;
  return out;
}
function buildImageUrl(val) { if (!val) return ""; if (/^https?:\/\//i.test(val)) return val; return `/news/bilder/${val}`; }

// NEU: separater Helper für Instagram-Bilder
function buildInstagramImageUrl(val) { if (!val) return ""; if (/^https?:\/\//i.test(val)) return val; return `/news/instagram/${val}`; }

const defaultMeta = {
  title: "",
  subtitle: "",
  cover: "",
  tags: "",
  kategorie: "",
  publishedAt: "",
  imageRaw: "",
  instagramImageRaw: "", // 🔥 NEU: Dateiname für Instagram-Bild
};

export default function ProtectedNewsArticleEditorYoopta({
  allowedRoleIds = [1, 2, 3],
  mode = "create",
  siteName = null,
  initialMeta = null,
  initialContentHtml = "",
  onSuccess,
}) {
  useInjectStyles(NY_CSS);
  const { user, token, roleId } = useAuth();

  const editor = useMemo(() => createYooptaEditor(), []);
  const previewEditor = useMemo(() => createYooptaEditor(), []);
  const TOOLS = useMemo(() => ({ Toolbar: { tool: Toolbar, render: DefaultToolbarRender }, ActionMenu: { tool: ActionMenu, render: DefaultActionMenuRender } }), []);

  const [meta, setMeta] = useState(initialMeta || defaultMeta);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [mainReady, setMainReady] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const readyHtmlRef = useRef(null);

  const authorized = allowedRoleIds.includes(Number(roleId));

  const putHtmlIntoEditor = (rawHtml) => {
    const stripped = stripAllWrappers(decodeIfEscaped(rawHtml || ""));
    const trySet = (v) => v && (editor.setEditorValue(v), true);
    try { if (trySet(yooptaHTML.deserialize(editor, stripped))) return; } catch { }
    try { if (trySet(yooptaHTML.deserialize(editor, reduceHtmlToBasicSubset(stripped)))) return; } catch { }
    editor.setEditorValue(yooptaHTML.deserialize(editor, "<p> </p>"));
  };

  useEffect(() => { if (mainReady && readyHtmlRef.current != null) { requestAnimationFrame(() => { putHtmlIntoEditor(readyHtmlRef.current); readyHtmlRef.current = null; }); } }, [mainReady]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (mode === "edit" && siteName) {
          setLoading(true); setError("");
          const res = await fetch(`/api/news/${encodeURIComponent(siteName)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
          if (!res.ok) throw new Error("Artikel konnte nicht geladen werden");
          const json = await res.json(); if (cancelled) return;

          setMeta({
            title: json?.headline ?? "",
            subtitle: json?.subline ?? "",
            cover: json?.image ?? "",
            imageRaw: json?.image ?? "",
            kategorie: json?.kategorie ?? "",
            tags: typeof json?.keywords === "string" ? json.keywords : "",
            publishedAt: json?.publishedAt ?? json?.created_at ?? json?.createdAt ?? "",
            // falls du später ein Feld in der API hast:
            instagramImageRaw: json?.instagram_image ?? json?.image_ig ?? "",
          });

          const rawHtml = json?.content || json?.contentHtml || json?.html || "";
          readyHtmlRef.current = rawHtml || "<p></p>";
          if (mainReady) { requestAnimationFrame(() => { putHtmlIntoEditor(rawHtml); readyHtmlRef.current = null; }); }
        } else {
          const startHtml = (initialContentHtml && initialContentHtml.trim()) ? initialContentHtml : "<p></p>";
          readyHtmlRef.current = startHtml;
          if (mainReady) { requestAnimationFrame(() => { putHtmlIntoEditor(startHtml); readyHtmlRef.current = null; }); }
          if (initialMeta) {
            setMeta(m => ({
              ...m,
              title: initialMeta.title ?? initialMeta.headline ?? m.title,
              subtitle: initialMeta.subline ?? initialMeta.subtitle ?? m.subtitle,
              cover: initialMeta.image ?? initialMeta.cover ?? m.cover,
              imageRaw: initialMeta.image ?? m.imageRaw,
              kategorie: initialMeta.kategorie ?? m.kategorie,
              tags: typeof initialMeta.keywords === "string" ? initialMeta.keywords : Array.isArray(initialMeta.tags) ? initialMeta.tags.join(", ") : (initialMeta.tags ?? m.tags),
              publishedAt: initialMeta.publishedAt ?? m.publishedAt,
              instagramImageRaw: initialMeta.instagram_image ?? initialMeta.image_ig ?? m.instagramImageRaw,
            }));
          }
        }
      } catch (e) { if (!cancelled) setError(e.message || "Fehler beim Laden."); }
      finally { if (!cancelled) setLoading(false); }
    };
    run(); return () => { cancelled = true; };
  }, [mode, siteName, token, initialContentHtml, initialMeta, mainReady, editor]);

  useEffect(() => {
    if (!showPreview || !previewReady) return;
    const snapshot = JSON.parse(JSON.stringify(editor.getEditorValue()));
    requestAnimationFrame(() => previewEditor.setEditorValue(snapshot));
  }, [showPreview, previewReady, editor, previewEditor]);

  const handleSave = async () => {
    if (!authorized) return;
    setSaving(true); setError("");
    try {
      const data = editor.getEditorValue();
      const html = yooptaHTML.serialize(editor, data);
      const inner = stripAllWrappers(html);

      const body = {
        author: user?.username || "",
        author_id: user?.id ?? null,
        headline: meta.title?.trim() || "",
        content: inner,
        image: (meta.imageRaw || meta.cover || "").trim(), // NUR Dateiname (webp) für Website
        instagram_image: (meta.instagramImageRaw || "").trim(), // 🔥 NEU: Dateiname für Instagram-Bild (optional)
        kategorie: meta.kategorie?.trim() || "",
        subline: meta.subtitle?.trim() || "",
        keywords: (typeof meta.tags === "string" ? meta.tags : Array.isArray(meta.tags) ? meta.tags.join(",") : "")
          .split(",").map(t => t.trim()).filter(Boolean).join(","),
      };

      const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const res = await fetch(mode === "edit" && siteName ? `/api/news/${encodeURIComponent(siteName)}` : `/api/news`, {
        method: mode === "edit" && siteName ? "PATCH" : "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.text()) || "Fehler beim Speichern");
      onSuccess?.({ ok: true });
    } catch (e) { setError(e.message || "Unerwarteter Fehler beim Speichern."); }
    finally { setSaving(false); }
  };

  const coverUrl = buildImageUrl(meta.imageRaw || meta.cover);
  if (loading) return <p style={{ padding: 16 }}>Editor lädt…</p>;

  return (
    <div className="ny-editor">
      <div className="ny-container">
        <div className="ny-back">
          <ReactLink to="/dashboard">&lsaquo; Zurück zum Dashboard</ReactLink>
        </div>
        <div className="ny-card">
          <div className="ny-header">
            <div>
              <div className="ny-title">News Artikel Editor</div>
            </div>
            <div className="ny-toolbar">
              <button className="btn" onClick={() => setShowPreview(true)}>Vorschau</button>
              {authorized && (
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? "Speichere…" : (mode === "edit" ? "Aktualisieren" : "Veröffentlichen")}
                </button>
              )}
            </div>
          </div>

          <div className="ny-grid">
            <section className="ny-section">
              <form className="ny-form" onSubmit={(e) => e.preventDefault()}>
                <div className="ny-field">
                  <label className="ny-label">Titel</label>
                  <input className="ny-input" placeholder="Artikel-Titel" value={meta.title}
                    onChange={(e) => setMeta(m => ({ ...m, title: e.target.value }))} disabled={!authorized} />
                </div>
                <div className="ny-field">
                  <label className="ny-label">Untertitel (subline)</label>
                  <input className="ny-input" placeholder="Untertitel" value={meta.subtitle}
                    onChange={(e) => setMeta(m => ({ ...m, subtitle: e.target.value }))} disabled={!authorized} />
                </div>
                <div className="ny-field">
                  <label className="ny-label">Kategorie (kategorie)</label>
                  <input className="ny-input" placeholder="Politik, Sport, …" value={meta.kategorie}
                    onChange={(e) => setMeta(m => ({ ...m, kategorie: e.target.value }))} disabled={!authorized} />
                </div>

                {/* Website Titelbild (WebP 1200x695) */}
                <div className="ny-field">
                  <label className="ny-label">Titelbild (immer WebP 1200×695)</label>
                  <UploadImage
                    uploadUrl="/api/news/upload-image"              // fester News-Endpunkt
                    headers={token ? { Authorization: `Bearer ${token}` } : {}}
                    fieldName="image"
                    accept="image/*"
                    // clientseitige Vorverarbeitung optional:
                    enforceSize={{ width: 1200, height: 695, mode: "cover", format: "image/webp", quality: 1 }}
                    value={meta.imageRaw || meta.cover}
                    onChange={(filename) => setMeta(m => ({ ...m, imageRaw: filename, cover: filename }))}
                    onRemove={() => setMeta(m => ({ ...m, imageRaw: "", cover: "" }))}
                    buildPreviewUrl={(fn) => buildImageUrl(fn)}
                    labels={{
                      title: "Titelbild hochladen",
                      select: "Datei wählen",
                      uploading: "Lade hoch…",
                      remove: "Entfernen",
                      hint: "Server erzwingt 1200×695 als WebP, Dateiname ist immer unique.",
                    }}
                  />
                </div>

                {/* NEU: Instagram Feed Bild (JPEG 1080x1350) */}
                <div className="ny-field">
                  <label className="ny-label">Instagram Feed Bild (JPEG 1080×1350)</label>
                  <UploadImage
                    uploadUrl="/api/news/upload-instagram-image"   // neuer Endpunkt im Backend
                    headers={token ? { Authorization: `Bearer ${token}` } : {}}
                    fieldName="image"
                    accept="image/*"
                    // optional, Client-Hinweis – Server setzt final Format/Größe
                    enforceSize={{ width: 1080, height: 1350, mode: "cover", format: "image/jpeg", quality: 0.9 }}
                    value={meta.instagramImageRaw}
                    onChange={(filename) => setMeta(m => ({ ...m, instagramImageRaw: filename }))}
                    onRemove={() => setMeta(m => ({ ...m, instagramImageRaw: "" }))}
                    buildPreviewUrl={(fn) => buildInstagramImageUrl(fn)}
                    labels={{
                      title: "Instagram Bild hochladen",
                      select: "Datei wählen",
                      uploading: "Lade hoch…",
                      remove: "Entfernen",
                      hint: "Wird für den automatischen Instagram-Post genutzt (JPEG 1080×1350).",
                    }}
                  />
                </div>

                <div className="ny-field">
                  <label className="ny-label">Tags (keywords, kommagetrennt)</label>
                  <input className="ny-input" placeholder="Tech, Open Source, Games" value={meta.tags}
                    onChange={(e) => setMeta(m => ({ ...m, tags: e.target.value }))} disabled={!authorized} />
                </div>

                <div className="ny-row">
                  <span className="ny-chip">Autor: {user?.username || "Unbekannt"}</span>
                  <span className="ny-chip">User-ID: {user?.id ?? "—"}</span>
                  <span className="ny-chip">
                    Veröffentlicht: {meta.publishedAt ? new Date(meta.publishedAt).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"} {meta.publishedAt ? new Date(meta.publishedAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) + " Uhr" : ""}
                  </span>
                </div>
              </form>
            </section>

            <section className="ny-section">
              {!authorized && <p className="ny-sub" style={{ margin: "8px 0" }}>Nur Ansicht. Melde dich mit ausreichenden Rechten an.</p>}
              <YooptaEditor
                className="ny-yoopta-wide"
                editor={authorized ? editor : previewEditor}
                plugins={PLUGINS}
                marks={MARKS}
                tools={authorized ? { Toolbar: { tool: Toolbar, render: DefaultToolbarRender }, ActionMenu: { tool: ActionMenu, render: DefaultActionMenuRender } } : undefined}
                readOnly={!authorized}
                autoFocus={authorized}
                placeholder={authorized ? 'Mit "/" das Block-Menü öffnen…' : undefined}
                spellCheck
              >
                <EditorReady onReady={() => authorized ? setMainReady(true) : setPreviewReady(true)} />
              </YooptaEditor>
            </section>
          </div>

          {error && <p style={{ color: "#dc2626", padding: 16, fontSize: 14 }}>{error}</p>}
        </div>
      </div>

      {showPreview && (
        <div className="ny-modal ny-modal--wide" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)" }} onClick={() => { setShowPreview(false); setPreviewReady(false); }}>
          <div className="ny-card" style={{ margin: "40px auto", background: "#fff" }}>
            <div className="ny-header">
              <div className="ny-title">Vorschau</div>
              <button className="ny-sub" onClick={() => { setShowPreview(false); setPreviewReady(false); }} aria-label="Schließen">×</button>
            </div>
            <div className="ny-section">
              <div className="ny-article">
                {coverUrl && <img className="ny-hero" src={coverUrl} alt={meta.title || "Titelbild"} />}
                <h1 className="ny-h1">{meta.title || "(Ohne Titel)"}</h1>
                {meta.subtitle && <p className="ny-sub">{meta.subtitle}</p>}
                <YooptaEditor className="ny-yoopta-wide" editor={previewEditor} plugins={PLUGINS} marks={MARKS} readOnly autoFocus={false}>
                  <EditorReady onReady={() => setPreviewReady(true)} />
                </YooptaEditor>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
